import { StateCreator } from 'zustand';

import { CommonStoreType, RealTimeServiceState } from '../types';
import {
  Bonus,
  BonusStage,
  Freespin,
  Jackpot,
  jackpots as fetchJackpots,
  JackpotStage,
  jackpotWinnersList as fetchJackpotWinnersList,
  Lootbox,
  playerBonuses,
  playerFreespins,
  playerLootboxes,
  playerRakebacks,
  PlayerTransaction,
} from '@/api/common';
import { RecentGame, recentGames as getRecentGames } from '@/api/games';
import { camelize } from '@/helpers/common';
import {
  findActiveLootboxId,
  isWheelLootbox,
  updateBonuses,
  updateRecentsWithNewGame,
} from '@/helpers/realTimeService';
// import { sendDepositSuccessEvent } from '@/modules/googleAnalytics';
import {
  findDiceLootboxes,
  findLootboxesWithoutTags,
  findScratchLootboxes,
  findWheelLootboxes,
  isDiceLootbox,
  isScratchLootbox,
} from '@/modules/promotions';

export const useRealTimeServiceSlice: StateCreator<
  CommonStoreType,
  [],
  [],
  RealTimeServiceState
> = (set, get) => {
  return {
    // jackpots
    activeJackpot: undefined,
    jackpotWinnersList: [],
    playerJackpotWins: [],
    pendingJackpots: false,
    pendingLootboxes: false,
    setActiveJackpot: async () => {
      try {
        set({ pendingJackpots: true });
        const { data: jackpotsData } = await fetchJackpots();

        const activeJackpot = jackpotsData?.find(
          (jackpot) => jackpot.state === JackpotStage.Active
        );

        if (!activeJackpot) return;

        return set({
          activeJackpot: camelize(activeJackpot) as Jackpot,
        });
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingJackpots: false });
      }
    },
    fetchJackpotWinnersList: async () => {
      try {
        const { data: jackpotWinnersList } = await fetchJackpotWinnersList();

        return set({ jackpotWinnersList });
      } catch (error) {
        console.log(error);
      }
    },
    addPlayerJackpotWin: (jackpotWin) =>
      set({ playerJackpotWins: [...get().playerJackpotWins, jackpotWin] }),

    //lootboxes
    wheelLootboxes: [],
    withoutTagsLootboxes: [],
    scratchLootboxes: [],
    diceLootboxes: [],
    activeLootboxId: undefined,

    fetchLootboxes: async () => {
      set({ pendingLootboxes: true });
      try {
        const { data: lootboxes } = await playerLootboxes();
        const { activeLootboxId } = get();

        const wheelLootboxes = findWheelLootboxes(lootboxes);
        const diceLootboxes = findDiceLootboxes(lootboxes);
        const scratchLootboxes = findScratchLootboxes(lootboxes);
        const withoutTagsLootboxes = findLootboxesWithoutTags(lootboxes);

        const newActiveLootboxId = findActiveLootboxId(withoutTagsLootboxes);

        set({
          wheelLootboxes,
          diceLootboxes,
          scratchLootboxes,
          withoutTagsLootboxes,
          ...(!activeLootboxId &&
            newActiveLootboxId && { activeLootboxId: newActiveLootboxId }),
        });
      } catch (error) {
        console.log(error);
      } finally {
        set({ pendingLootboxes: false });
      }
    },
    updateLootboxes: (lootbox: Lootbox) => {
      if (isWheelLootbox(lootbox)) {
        return set({
          wheelLootboxes: [...get().wheelLootboxes, { ...lootbox, live: true }],
        });
      }

      if (isDiceLootbox(lootbox)) {
        return set({
          diceLootboxes: [...get().diceLootboxes, { ...lootbox, live: true }],
        });
      }

      if (isScratchLootbox(lootbox)) {
        return set({
          scratchLootboxes: [
            ...get().scratchLootboxes,
            { ...lootbox, live: true },
          ],
        });
      }

      set({
        withoutTagsLootboxes: [
          ...get().withoutTagsLootboxes,
          { ...lootbox, live: true },
        ],
      });
    },

    //bonuses
    bonusesPending: false,
    bonuses: [],
    fetchBonuses: async () => {
      try {
        set({ bonusesPending: true });
        const { data: bonuses } = await playerBonuses();

        set({ bonuses, bonusesPending: false });
      } catch (error) {
        console.log(error);
      }
    },
    updateBonuses: (incomingBonus: Bonus) => {
      const { bonuses } = get();

      const updatedBonuses = updateBonuses(bonuses, incomingBonus);

      set({
        bonuses: updatedBonuses,
      });
    },

    //rakebackBonus
    rakebacks: [],
    fetchRakebacks: async () => {
      try {
        const { data: rakebacks } = await playerRakebacks();

        set({ rakebacks });
      } catch (error) {
        console.log(error);
      }
    },

    //freespins
    freespinsPending: false,
    freespins: [],
    hasUserUsedFreespins: null,
    fetchFreespins: async () => {
      try {
        set({ freespinsPending: true });
        const { data: freespins = [] } = await playerFreespins();

        const hasUserUsedFreespins = freespins.some(({ stage }) => {
          return [BonusStage.Finished, BonusStage.Activated].includes(stage);
        });

        set({ freespins, hasUserUsedFreespins });
      } catch (error) {
        console.log(error);
      } finally {
        set({ freespinsPending: false });
      }
    },
    updateFreespins: (incomingFreespin: Freespin) => {
      const { freespins } = get();

      const updatedFreespins = updateBonuses(freespins, incomingFreespin);

      set({
        freespins: updatedFreespins,
      });
    },

    //payments
    playerPayments: [],
    updatePlayerPayments: (payment: PlayerTransaction) => {
      set({
        playerPayments: [...get().playerPayments, payment],
      });
      // DEV-727: temporarily disabled Google Analytics deposit event tracking
      // if (payment.action === 'deposit' && payment.success) {
      //   try {
      //     const deposits = Object.values(
      //       get().lastSuccessfulPaymentsByCurrency || {}
      //     );
      //     sendDepositSuccessEvent({
      //       type: deposits.length > 0 ? 'std' : 'ftd',
      //       amount: payment.amountCents,
      //       currency: payment.currency,
      //     });
      //   } catch (error) {
      //     console.error('Error sending deposit success event:', error);
      //   }
      // }
    },

    //games
    recentGamesIds: [],
    fetchRecentGamesIds: async () => {
      try {
        const { data: recentGames } = await getRecentGames();
        if (!recentGames) return;

        set({
          recentGamesIds: recentGames.map(({ identifier }) => identifier),
        });
      } catch (error) {
        console.log(error);
      }
    },
    updateRecentGames: (recentGame: RecentGame) => {
      set({
        recentGamesIds: updateRecentsWithNewGame(
          get().recentGamesIds,
          recentGame
        ),
      });
    },
  };
};
